import React, { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import * as THREE from 'three';
import { MarchingCubes } from 'three/examples/jsm/objects/MarchingCubes';
import Vertex from './vertex.glsl';
import Fragment from './fragment.glsl';

const HeroContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  pointer-events: none;
`;

const Hero = () => {
  const containerRef = useRef(null);
  const ballMeshes = useRef([]);
  const ballPositions = useRef([]);
  const ballVelocities = useRef([]);
  const releasePositions = useRef([]);
  const prevHoveredBallIndex = useRef(-1); // Add this line
  const mouse = useRef(new THREE.Vector2());
  const mouseTargetPosition = useRef(new THREE.Vector3());
  const cameraRef = useRef(null); // Add ref for camera

  // Add function to update camera position based on screen width
  const updateCameraPosition = useCallback(() => {
    if (!cameraRef.current) return;

    const width = window.innerWidth;

    // Camera distance constants
    const MOBILE = 65;
    const TABLET = 50;
    const NORMAL = 40;
    const DESKTOP = 30;

    // Set camera position based on breakpoints
    if (width <= 768) {
      cameraRef.current.position.z = MOBILE;
    } else if (width <= 1000) {
      cameraRef.current.position.z = TABLET;
    } else if (width <= 1300) {
      cameraRef.current.position.z = NORMAL;
    } else {
      cameraRef.current.position.z = DESKTOP;
    }
  }, []);

  useEffect(() => {
    const raycaster = new THREE.Raycaster();

    const container = containerRef.current;
    let width = container.clientWidth;
    let height = container.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff); // Set background color to white
    scene.fog = new THREE.Fog(0xc0f0ff, 0.15);

    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
    //camera.position.z = 30;
    cameraRef.current = camera; // Store camera reference

    // Initial camera position update
    updateCameraPosition();

    const light = new THREE.HemisphereLight(0xffffff, 0x444444, 1);
    scene.add(light);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);

    const materials = generateMaterials();
    let currentMaterial = 'shader';
    let resolution = 50;

    const effect = new MarchingCubes(
      resolution,
      materials[currentMaterial],
      true,
      true,
      100000
    );
    effect.position.set(0, 0, 0);
    const scale = 40;
    effect.scale.set(scale, scale, scale);
    effect.enableUvs = false;
    effect.enableColors = false;
    scene.add(effect);

    const effectController = {
      material: 'shader',
      speed: 1.0,
      numBlobs: 5,
      resolution: 50,
      isolation: 250,
      floor: false,
      wallx: false,
      wallz: false,
      dummy: function () {},
    };

    const ballGeometry = new THREE.SphereGeometry(3, 32, 32);
    const ballMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const numblobs = effectController.numBlobs;

    for (let i = 0; i < numblobs; i++) {
      const ballMesh = new THREE.Mesh(ballGeometry, ballMaterial);
      ballMesh.visible = false;
      scene.add(ballMesh);
      ballMeshes.current[i] = ballMesh;
      ballPositions.current[i] = new THREE.Vector3();
      ballVelocities.current[i] = new THREE.Vector3();
      releasePositions.current[i] = new THREE.Vector3();
    }

    const clock = new THREE.Clock();
    let time = 0.0;

    function generateMaterials() {
      const materials = {
        shader: new THREE.ShaderMaterial({
          uniforms: {
            uColor: { value: new THREE.Color(0x42a9f1) },
            uResolution: {
              value: new THREE.Vector2(window.innerWidth, window.innerHeight),
            },
            viewVector: { value: camera.position },
            outlineThickness: { value: 3 }, // Adjust the thickness as needed
            outlineColor: { value: new THREE.Color(0x000000) }, // Black color
          },
          vertexShader: Vertex,
          fragmentShader: Fragment,
          side: THREE.DoubleSide,
          transparent: true,
          wireframe: false,
        }),
      };
      return materials;
    }

    function updateCubes(object, time, numBlobs, floor, wallx, wallz) {
      object.reset();

      const subtract = 12;
      const strength = 1.2 / ((Math.sqrt(numBlobs) - 1) / 4 + 1);

      for (let i = 0; i < numBlobs; i++) {
        const ballPosition = ballPositions.current[i] || new THREE.Vector3();
        const ballVelocity = ballVelocities.current[i] || new THREE.Vector3();

        if (prevHoveredBallIndex.current === i) {
          // Ball follows the mouse position
          const direction = mouseTargetPosition.current
            .clone()
            .sub(ballPosition)
            .normalize();
          const speed = 0.05; // Speed of the ball following the mouse
          ballVelocity.add(direction.multiplyScalar(speed));
        } else {
          // New organic ball shape animation
          const angle = (i / numBlobs) * Math.PI * 2 + time * 0.02; // Slower animation speed
          const radius = 1 + Math.sin(time * 0.5 + i * 0.8) * 2; // Larger, smoother radius
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          const z = Math.sin(time * 0.5 + i * 0.8) * 0.2; // Adding a bit of z-motion for more 3D effect

          const targetPosition = new THREE.Vector3(x, y, z);
          const direction = targetPosition
            .clone()
            .sub(ballPosition)
            .normalize();
          const speed = 0.01; // Slower speed for normal animation
          ballVelocity.add(direction.multiplyScalar(speed));
        }

        ballPosition.add(ballVelocity);
        ballVelocity.multiplyScalar(0.99); // Damping factor to slow down the velocity over time

        const ballx = ballPosition.x / scale + 0.49;
        const bally = ballPosition.y / scale + 0.5;
        const ballz = ballPosition.z / scale + 0.5;

        object.addBall(ballx, bally, ballz, strength, subtract);
        object.material.uniforms.viewVector.value = camera.position;
        object.material.uniformsNeedUpdate = true;

        ballMeshes.current[i].position.copy(ballPosition.clone());

        ballPositions.current[i] = ballPosition;
        ballVelocities.current[i] = ballVelocity;
      }

      if (floor) object.addPlaneY(2, 12);
      if (wallz) object.addPlaneZ(2, 12);
      if (wallx) object.addPlaneX(2, 12);

      object.update();
    }

    function onMouseMove(event) {
      mouse.current.x = (event.clientX / width) * 2 - 1;
      mouse.current.y = -(event.clientY / height) * 2 + 1;

      raycaster.setFromCamera(mouse.current, camera);
      const intersects = raycaster.intersectObjects(ballMeshes.current);

      if (intersects.length > 0) {
        const hoveredBall = intersects[0].object;
        const hoveredBallIndex = ballMeshes.current.indexOf(hoveredBall);

        if (hoveredBallIndex !== -1) {
          //console.log('Hovered Ball Index:', hoveredBallIndex); // Log the hovered ball index
          prevHoveredBallIndex.current = hoveredBallIndex;

          // Calculate the mouse position in world coordinates
          const mouseWorldPosition = new THREE.Vector3(
            mouse.current.x,
            mouse.current.y,
            0
          ).unproject(camera);
          mouseWorldPosition.z = 0; // Ensure the ball stays in the x-y plane
          mouseTargetPosition.current = mouseWorldPosition;
        }
      } else {
        prevHoveredBallIndex.current = -1;
      }
    }

    function renderLoop() {
      const delta = clock.getDelta();
      time += delta * effectController.speed * 0.1;

      if (effectController.resolution !== resolution) {
        resolution = effectController.resolution;
        effect.init(Math.floor(resolution));
      }

      if (effectController.isolation !== effect.isolation) {
        effect.isolation = effectController.isolation;
      }

      updateCubes(
        effect,
        time,
        effectController.numBlobs,
        effectController.floor,
        effectController.wallx,
        effectController.wallz
      );

      //   // Update fog particle positions
      //   const positions = particleSystem.geometry.attributes.position.array;
      //   const velocities = particleSystem.geometry.attributes.velocity.array;

      //   for (let i = 0; i < particleCount; i++) {
      //     positions[i * 3] += velocities[i * 3];
      //     positions[i * 3 + 1] += velocities[i * 3 + 1];
      //     positions[i * 3 + 2] += velocities[i * 3 + 2];

      //     // Wrap around when particles go out of bounds
      //     if (positions[i * 3] > 100 || positions[i * 3] < -100)
      //       velocities[i * 3] *= -1;
      //     if (positions[i * 3 + 1] > 50 || positions[i * 3 + 1] < -50)
      //       velocities[i * 3 + 1] *= -1;
      //     if (positions[i * 3 + 2] > 100 || positions[i * 3 + 2] < -100)
      //       velocities[i * 3 + 2] *= -1;
      //   }

      //   particleSystem.geometry.attributes.position.needsUpdate = true;

      renderer.render(scene, camera);
      requestAnimationFrame(renderLoop);
    }

    function handleResize() {
      width = container.clientWidth;
      height = container.clientHeight;

      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      updateCameraPosition();

      renderer.setSize(width, height);
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', onMouseMove);

    renderLoop();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.addEventListener('mousemove', onMouseMove);

      container.removeChild(renderer.domElement);
    };
  }, [updateCameraPosition]);

  return <HeroContainer ref={containerRef} />;
};

export default Hero;
